import styled from "@emotion/styled";
import React from "react";
import { BLACK, WHITE } from "../../design-tokens";

const SpinnerBlack = ({
  dataTestId,
  className,
  width,
  height,
}: {
  dataTestId: string;
  className: string;
  width: string;
  height: string;
}) => {
  return (
    <svg
      data-testid={dataTestId}
      className={className}
      style={{ width: width, height: height }}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6 1C5.0111 1 4.0444 1.29324 3.22215 1.84265C2.39991 2.39206 1.75904 3.17295 1.3806 4.08658C1.00217 5.00021 0.90315 6.00555 1.09608 6.97545C1.289 7.94536 1.76521 8.83627 2.46447 9.53553C3.16373 10.2348 4.05465 10.711 5.02455 10.9039C5.99446 11.0969 6.99979 10.9978 7.91342 10.6194C8.82705 10.241 9.60794 9.6001 10.1574 8.77785C10.7068 7.95561 11 6.98891 11 6C11 5.72386 11.2239 5.5 11.5 5.5C11.7761 5.5 12 5.72386 12 6C12 7.18669 11.6481 8.34673 10.9888 9.33342C10.3295 10.3201 9.39246 11.0892 8.2961 11.5433C7.19975 11.9974 5.99335 12.1162 4.82946 11.8847C3.66557 11.6532 2.59648 11.0818 1.75736 10.2426C0.918247 9.40353 0.346802 8.33443 0.115291 7.17054C-0.11622 6.00666 0.00259969 4.80026 0.456726 3.7039C0.910851 2.60754 1.67989 1.67047 2.66658 1.01118C3.65328 0.351894 4.81331 0 6 0C6.27614 0 6.5 0.223858 6.5 0.5C6.5 0.776142 6.27614 1 6 1Z"
        fill="url(#paint0_linear_751_3187)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_751_3187"
          x1="6"
          y1="0"
          x2="6"
          y2="12"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="black" stopOpacity="0" />
          <stop offset="1" stopColor="black" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const SpinnerWhite = ({
  dataTestId,
  className,
  width,
  height,
}: {
  dataTestId: string;
  className: string;
  width: string;
  height: string;
}) => {
  return (
    <svg
      data-testid={dataTestId}
      className={className}
      style={{ width: width, height: height }}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6 1C5.0111 1 4.0444 1.29324 3.22215 1.84265C2.39991 2.39206 1.75904 3.17295 1.3806 4.08658C1.00217 5.00021 0.90315 6.00555 1.09608 6.97545C1.289 7.94536 1.76521 8.83627 2.46447 9.53553C3.16373 10.2348 4.05465 10.711 5.02455 10.9039C5.99446 11.0969 6.99979 10.9978 7.91342 10.6194C8.82705 10.241 9.60794 9.6001 10.1574 8.77785C10.7068 7.95561 11 6.98891 11 6C11 5.72386 11.2239 5.5 11.5 5.5C11.7761 5.5 12 5.72386 12 6C12 7.18669 11.6481 8.34673 10.9888 9.33342C10.3295 10.3201 9.39246 11.0892 8.2961 11.5433C7.19975 11.9974 5.99335 12.1162 4.82946 11.8847C3.66557 11.6532 2.59648 11.0818 1.75736 10.2426C0.918247 9.40353 0.346802 8.33443 0.115291 7.17054C-0.11622 6.00666 0.00259969 4.80026 0.456726 3.7039C0.910851 2.60754 1.67989 1.67047 2.66658 1.01118C3.65328 0.351894 4.81331 0 6 0C6.27614 0 6.5 0.223858 6.5 0.5C6.5 0.776142 6.27614 1 6 1Z"
        fill="url(#paint0_linear_751_3187)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_751_3187"
          x1="6"
          y1="0"
          x2="6"
          y2="12"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity="0" />
          <stop offset="1" stopColor="white" />
        </linearGradient>
      </defs>
    </svg>
  );
};

type LoadingIndicatorProps = {
  className?: string;
  color?: "white" | "black";
  height?: number;
  width?: number;
  fullScreen?: boolean;
  message?: string;
  showLoadingMessage?: boolean;
  inline?: boolean;
};

const LoadingIndicator = ({
  className,
  color = "white",
  height = 30,
  width = 30,
  fullScreen,
  message = "Loading...",
  showLoadingMessage = true,
  inline = false,
}: LoadingIndicatorProps) => {
  let spinner;
  if (fullScreen) height = width = 50;
  switch (color) {
    case "black":
      spinner = (
        <SpinnerBlackAnim
          dataTestId="black-spinner"
          className={className}
          height={`${height}px`}
          width={`${width}px`}
        />
      );
      break;
    case "white":
      spinner = (
        <SpinnerWhiteAnim
          dataTestId="white-spinner"
          className={className}
          height={`${height}px`}
          width={`${width}px`}
        />
      );
      break;
  }

  if (fullScreen) {
    return (
      <FullscreenSpinnerContainer data-testid="fullscreen-loading-indicator">
        {spinner}
        {showLoadingMessage && <div>{message}</div>}
      </FullscreenSpinnerContainer>
    );
  }

  return (
    <SpinnerContainer inline={inline}>
      {spinner}
      {showLoadingMessage && (
        <LoadingMessage color={color}>{message}</LoadingMessage>
      )}
    </SpinnerContainer>
  );
};

const LoadingMessage = styled.div<{ color: "white" | "black" }>`
  color: ${({ color }) => (color === "white" ? WHITE : BLACK)};
`;

const SpinnerContainer = styled.div<{ inline: boolean }>`
  display: ${({ inline }) => (inline ? "inline" : "flex")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  > div {
    padding-top: 10px;
  }
`;

const FullscreenSpinnerContainer = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  > div {
    padding-top: 10px;
  }
`;

const SpinnerBlackAnim = styled(SpinnerBlack)`
  @keyframes spin {
    from {
      transform: scaleX(-1) rotate(0turn);
    }
    to {
      transform: scaleX(-1) rotate(-1turn);
    }
  }
  animation: spin;
  animation-duration: 5s;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
`;
const SpinnerWhiteAnim = styled(SpinnerWhite)`
  transform: scaleX(-1);

  @keyframes spin {
    from {
      transform: scaleX(-1) rotate(0turn);
    }
    to {
      transform: scaleX(-1) rotate(-1turn);
    }
  }
  animation: spin;
  animation-duration: 5s;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
`;

export type { LoadingIndicatorProps };
export { LoadingIndicator };
